import React, { useMemo, useState } from 'react';
import {
  AvDialog,
  ButtonTypes,
  DescriptorType,
  FeatureFlags,
  FieldListObject,
  FiltersListToolbar,
  Select,
  SelectedFieldsType,
  TextInput,
} from '@fabric-ui/components';
import { Search } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box, Divider } from '@mui/material';
import Button from '@mui/material/Button';
import { useAvContext } from '../context/AvContextProvider';
import { getMatchStringValuesInExpression } from '../utils/utils';
import { getKeyByValue } from '../views/CustomDashboards/components/SelectWidgetType/SelectWidgetType';
import { inputInnerLabelStartAdornment } from '../views/CustomDashboards/CustomDashboardsUtils';
import EntityItem from '../views/CustomDashboards/EditCustomDashboard/EntityItem';
import { timeBucketOptions } from '../views/CustomDashboards/EditCustomDashboard/Tabs/constants';
import { FieldType } from '../views/CustomDashboards/types/types';
import { useCalculatedDimensionsList, useMeasurementsList } from '../views/Reports/hooks';
import { FieldsToFilter } from '../views/Reports/reports.utils';
import FiltersEmptyState from './FiltersEmptyState';
import useFilterFields from './useFilterFields';

const isIncludedInSearch = (field, search) => field.title.toLowerCase().includes(search.toLowerCase());

const getFilteredDims = (dims: any[]) => dims.filter(field => !FieldsToFilter.includes(field.systemName));

interface Props {
  activeProjectionName: string;
  changeEntity: {
    enableEntityChange?: boolean;
    checkIfToOpenDialog: (selectedProjName: string) => boolean;
    acceptEntityChange: (newProjectionName: string) => void;
    popupText: string;
  };
  onChangeFilterType?: (event: React.MouseEvent<HTMLElement> | null, fieldsTypes: string) => any;
  selectFields: {
    selectedFields: SelectedFieldsType;
    onFieldClick: (item: any, fieldType: FieldType) => void;
    onApplyExpression?: ({ field, type, selectedValue }: { field: FieldListObject; type: string; selectedValue: any }) => void;
  };
  defaultExpandedEntities: Record<string, boolean>;
  isHistoricalActive: boolean;
  hiddenButtons?: Set<ButtonTypes>;
  page: string;
  shouldDisableField?: (field: FieldListObject) => { disabled: boolean; disabledTooltip: string };
  enabledFieldTypesWithOptions?: Set<string | undefined>;
}

export default function FieldsToolbar({
  activeProjectionName,
  changeEntity: { enableEntityChange = true, checkIfToOpenDialog, acceptEntityChange, popupText },
  onChangeFilterType,
  selectFields: { selectedFields, onFieldClick, onApplyExpression },
  defaultExpandedEntities,
  isHistoricalActive,
  hiddenButtons,
  page,
  shouldDisableField,
  enabledFieldTypesWithOptions,
}: Props) {
  const {
    accountData: {
      flags: { isRecommendedFieldsEnabled },
    },
    accountEntities: { aggProjs },
    featureFlags,
    measurements: avContextMeasurements,
  } = useAvContext();

  const { name: entityName } = aggProjs[activeProjectionName]?.entityTypeId ?? {};

  const { data: measurements = [] } = useMeasurementsList({
    entityName,
    filterFunc: v => v.context.includes(entityName),
  });

  const { data: calculatedDimensions = [] } = useCalculatedDimensionsList({
    projectionName: aggProjs[activeProjectionName]?.projId.name,
    filterFunc: v => v.context?.some(ctx => ctx.name === aggProjs[activeProjectionName]?.projId.name),
    enabled: !!aggProjs[activeProjectionName],
  });

  const filteredMeasurements = useMemo(
    () =>
      featureFlags[FeatureFlags.HistoricalAnalytics]
        ? isHistoricalActive
          ? measurements.filter(measurement => measurement.timeExpression)
          : measurements.filter(measurement => measurement.expression)
        : measurements,
    [isHistoricalActive, activeProjectionName]
  );

  const measurementsList = useMemo(
    () =>
      filteredMeasurements.map(({ systemName, displayName, isRecommended }) => ({
        value: systemName,
        pathAlias: systemName,
        title: displayName,
        group: getKeyByValue(FieldType, FieldType.Measurements),
        entityTypeId: { builtin: true },
        isRecommended,
      })),
    [filteredMeasurements]
  );

  const calculatedDimensionsList = useMemo(
    () =>
      !featureFlags[FeatureFlags.CalculatedDimensions] || isHistoricalActive
        ? []
        : calculatedDimensions.map(({ systemName, displayName, type }) => ({
            value: systemName,
            pathAlias: systemName,
            title: displayName,
            group: 'Calculated Dimensions',
            entityTypeId: { builtin: true },
            type: type?.kindWrapper?.kind ? DescriptorType[type.kindWrapper.kind] : undefined,
          })),
    [calculatedDimensions, isHistoricalActive, featureFlags]
  );

  const [search, setSearch] = useState('');
  const [selectedEntity, setSelectedEntity] = useState('');
  const [expandedEntities, setExpandedEntities] = useState(defaultExpandedEntities);

  const filteredDims = useMemo(
    () => [...(isHistoricalActive ? timeBucketOptions : []), ...getFilteredDims(aggProjs[activeProjectionName]?.fieldList.INTERACTIVE)],
    [activeProjectionName, isHistoricalActive]
  );

  const { fields, resetFilters, activeFilterType, ...filtersProps } = useFilterFields({
    dims: hiddenButtons?.has(ButtonTypes.Dimensions) ? [] : filteredDims,
    metrics: measurementsList,
    calcDims: isHistoricalActive ? [] : calculatedDimensionsList,
    selectedFields,
    onChangeFilterType,
    topFieldsEnabled: featureFlags[FeatureFlags.TopFields] && isRecommendedFieldsEnabled,
    populatedFieldsEnabled: featureFlags[FeatureFlags.PopulatedFields] && isRecommendedFieldsEnabled,
    isHistoricalActive,
  });

  const fieldsFilteredBySearch = useMemo(() => fields.filter(field => isIncludedInSearch(field, search.toLowerCase())), [fields, search]);

  const fieldsPerEntity = useMemo(() => {
    const grouped = fieldsFilteredBySearch.reduce((acc, field) => {
      const key = field.group.split(' Custom Fields')[0];
      return { ...acc, [key]: [...(acc[key] || []), field] };
    }, {});

    // Remove Calculated Dimensions group when it's empty (no calculated dimensions available)
    if (grouped['Calculated Dimensions'] && grouped['Calculated Dimensions'].length === 0) {
      const { 'Calculated Dimensions': removed, ...rest } = grouped;
      return rest;
    }

    return grouped;
  }, [fieldsFilteredBySearch, isHistoricalActive, featureFlags]);

  // Custom ordering for groups: All dimension groups first (alphabetically), then Calculated Dimensions, then Measurements
  const orderedGroupKeys = useMemo(() => {
    const keys = Object.keys(fieldsPerEntity);
    const calculatedDimensionsKey = keys.find(k => k === 'Calculated Dimensions');
    const measurementsKey = keys.find(k => k === 'Measurements');

    // All groups except Calculated Dimensions and Measurements are dimension groups
    const dimensionGroups = keys.filter(k => k !== 'Calculated Dimensions' && k !== 'Measurements').sort();

    return [
      ...dimensionGroups,
      ...(calculatedDimensionsKey ? [calculatedDimensionsKey] : []),
      ...(measurementsKey ? [measurementsKey] : []),
    ];
  }, [fieldsPerEntity, isHistoricalActive, featureFlags]);

  const matchHighlightResults = useMemo(() => getMatchStringValuesInExpression(search).filter(v => !!v), [search]);

  const onChangeEntity = (selectedProjName: string) => {
    const openDialog = checkIfToOpenDialog(selectedProjName);

    if (openDialog) {
      setSelectedEntity(selectedProjName);
    } else {
      acceptEntityChange(selectedProjName);
    }
  };

  const onClosePopup = () => setSelectedEntity('');
  const onAcceptPopup = () => {
    acceptEntityChange(selectedEntity);
    onClosePopup();
  };

  return (
    <>
      <AvDialog
        open={!!selectedEntity}
        onClose={onClosePopup}
        title="Change Main Entity"
        footer={
          <Box sx={{ ...flex.justifyEndCenter, gap: 2 }}>
            <Button onClick={onClosePopup}>Cancel</Button>
            <Button variant="contained" onClick={onAcceptPopup}>
              Yes
            </Button>
          </Box>
        }>
        <div>{popupText}</div>
      </AvDialog>

      <Box sx={{ ...flex.col }}>
        <Box sx={{ ...flex.col, gap: '10px' }}>
          {enableEntityChange && (
            <>
              <Select
                startAdornment={inputInnerLabelStartAdornment('Main Entity')}
                muiProps={{ disableClearable: true }}
                size="xSmall"
                value={aggProjs[activeProjectionName].projId.name}
                onChange={onChangeEntity}
                options={Object.values(aggProjs).map(({ projDisplayName, projId }) => ({
                  title: projDisplayName,
                  value: projId.name,
                }))}
              />

              <Divider sx={{ maxWidth: '300px' }} />
            </>
          )}
          <TextInput
            size="xSmall"
            helperText="Search..."
            icon={<Search style={{ ...iconSize(16) }} />}
            value={search}
            onChange={v => setSearch(v.toLowerCase())}
          />
        </Box>

        <FiltersListToolbar activeFilterType={activeFilterType} hiddenButtons={hiddenButtons} {...filtersProps} />
      </Box>

      <Box sx={{ height: '100%', overflow: 'auto' }}>
        {!fieldsFilteredBySearch.length ? (
          <FiltersEmptyState
            onClick={resetFilters}
            activeFilterType={activeFilterType}
            isTopFieldsFilterActive={filtersProps.topFields.isTopFieldsFilterActive}
            isInUseFilterActive={filtersProps.inUse.isInUseFilterActive}
            isUnpopulatedFieldsActive={filtersProps.unpopulated.isUnpopulatedFieldsActive}
          />
        ) : (
          orderedGroupKeys.map(key => {
            if (fieldsPerEntity[key][0]) {
              const {
                group,
                entityTypeId: { builtIn },
              } = fieldsPerEntity[key][0];
              const displayName = builtIn ? group : group.replace('uber_', '');
              if (fieldsPerEntity[key].length) {
                return (
                  <EntityItem
                    key={key}
                    displayName={displayName}
                    fieldList={fieldsPerEntity[key]}
                    expanded={expandedEntities[displayName] || matchHighlightResults.length}
                    handleExpand={entityToExpand => setExpandedEntities(prev => ({ ...prev, [entityToExpand]: !prev[entityToExpand] }))}
                    fieldType={
                      key === 'Measurements'
                        ? FieldType.Measurements
                        : key === 'Calculated Dimensions'
                          ? FieldType.Dimensions
                          : FieldType.Dimensions
                    }
                    matchHighlightResults={matchHighlightResults}
                    onFieldClick={onFieldClick}
                    selectedFields={selectedFields}
                    page={page}
                    measurementsObject={{ ...avContextMeasurements.asObject }}
                    shouldDisableField={shouldDisableField}
                    enabledFieldTypesWithOptions={enabledFieldTypesWithOptions}
                    onApplyExpression={onApplyExpression}
                  />
                );
              }
            }
            return undefined;
          })
        )}
      </Box>
    </>
  );
}
